/**
 * Atlas PM · 顶栏用户菜单（可复用组件）
 *
 * P6.55：从 AppShell 内联 JSX 抽取为独立组件，供「普通页面」与
 * 「满屏指挥视图（全国态势 / 项目星图）」两处共用，避免复制两份逻辑。
 *
 * 内容：
 *   - 身份信息（显示名 / @用户名 / 角色）
 *   - 按权限出现的导航入口：
 *       · 有 user:manage / role:manage / dict:manage → 「系统设置」
 *       · 有 audit:view                          → 「操作日志」
 *       · 无任何管理权限（如操作员）             → 只显示身份信息 + 退出登录
 *   - 退出登录（行为不变：调 auth store 的 logout()）
 *
 * 交互：点击菜单外区域关闭；Esc 关闭；触发按钮保留 aria-haspopup / aria-expanded。
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';

/** 进入「系统设置」所需的管理类权限（任一满足） */
const MANAGE_PERMISSIONS: readonly string[] = ['user:manage', 'role:manage', 'dict:manage'];

export default function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  /* 点击菜单外区域 / 按 Esc → 关闭 */
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const initials = (user?.displayName || user?.username || '?').slice(0, 1).toUpperCase();
  const canManage = hasAnyPermission([...MANAGE_PERMISSIONS]);
  const canAudit = hasPermission('audit:view');

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  const onLogout = () => {
    setOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="usermenu" ref={rootRef}>
      <button
        type="button"
        className="usermenu__trigger"
        data-testid="usermenu-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="usermenu__avatar topbar__avatar">{initials}</span>
        <span className="usermenu__name">{user?.displayName ?? '未登录'}</span>
      </button>

      {open && (
        <div className="usermenu__pop" role="menu" data-testid="usermenu-pop">
          <div className="usermenu__meta" data-testid="usermenu-meta">
            <b>{user?.displayName}</b>
            <span>@{user?.username}</span>
            <span>{(user?.roles ?? []).join(' · ') || '无角色'}</span>
          </div>

          {canManage && (
            <button
              type="button"
              role="menuitem"
              className="usermenu__item"
              data-testid="usermenu-settings"
              onClick={() => go('/settings')}
            >
              系统设置
            </button>
          )}

          {canAudit && (
            <button
              type="button"
              role="menuitem"
              className="usermenu__item"
              data-testid="usermenu-audit"
              onClick={() => go('/settings/audit')}
            >
              操作日志
            </button>
          )}

          <button
            type="button"
            role="menuitem"
            className="usermenu__item"
            data-testid="usermenu-logout"
            onClick={onLogout}
          >
            退出登录
          </button>
        </div>
      )}
    </div>
  );
}
